// Request bodies and frozen snapshot shapes of the billing flow (spec §1.10, §1.12). In src/shared so the
// future client validates against the same objects; every message is the German sentence the form shows.
import { z } from 'zod'
import { VAT_MODES } from './profil'
import { addressSchema } from './tenant'

const id = z.string().trim().min(1).max(64)
const isoDate = z.string().regex(/^\d{4}-\d{2}-\d{2}$/, { message: 'Bitte ein Datum im Format JJJJ-MM-TT angeben.' })
const cents = z.number().int({ message: 'Beträge werden in ganzen Cent angegeben.' })

/**
 * A hard cap on one Abrechnung. The largest legacy batch is 27 lines; 500 is far above any real month and
 * keeps the draft batch (one UPDATE per added and per removed line) inside a size a Worker commits happily.
 */
export const MAX_DRAFT_LINES = 500

/** POST /api/me/rechnungen — the idempotent upsert of the recipient's single draft. */
export const entwurfSchema = z.object({
  tenant_contractor_id: id,
  commission_ids: z
    .array(id)
    .max(MAX_DRAFT_LINES, { message: `Höchstens ${MAX_DRAFT_LINES} Positionen pro Abrechnung.` }),
})
export type EntwurfBody = z.infer<typeof entwurfSchema>

/** POST /api/me/rechnungen/:invId/submit — the consent box must be ticked. */
export const submitSchema = z.object({
  consent: z.boolean().refine((v) => v, { message: 'Bitte bestätige die Einverständniserklärung.' }),
})
export type SubmitBody = z.infer<typeof submitSchema>

/** POST /api/t/:id/rechnungen/:invId/reject — the comment is what the Auftragnehmer reads in the mail. */
export const rejectSchema = z.object({
  comment: z
    .string()
    .trim()
    .min(3, { message: 'Bitte begründe die Ablehnung (mindestens 3 Zeichen).' })
    .max(500),
})
export type RejectBody = z.infer<typeof rejectSchema>

/** POST /api/t/:id/rechnungen/:invId/payout — one invoice = one transfer, no partial payouts. */
export const payoutSchema = z.object({
  paid_at: isoDate,
  amount_cents: cents,
  bank_reference: z
    .string()
    .trim()
    .min(1, { message: 'Bitte den Verwendungszweck oder die Referenz der Überweisung angeben.' })
    .max(140),
})
export type PayoutBody = z.infer<typeof payoutSchema>

/** POST /api/t/:id/rechnungen/:invId/cancel — the reason is printed in the audit trail, not on the document. */
export const cancelSchema = z.object({
  reason: z
    .string()
    .trim()
    .min(3, { message: 'Bitte begründe die Stornierung (mindestens 3 Zeichen).' })
    .max(500),
})
export type CancelBody = z.infer<typeof cancelSchema>

/**
 * `contractor_invoices.issuer_snapshot_json` — the Auftragnehmer as he was at approval (spec §1.10 step 3).
 * Every renderer and the DATEV export read this, never the live `contractors` row.
 */
export const issuerSnapshotSchema = z.object({
  legal_name: z.string(),
  legal_form: z.string().nullable(),
  representative_name: z.string().nullable(),
  address: addressSchema,
  country: z.string(),
  vat_mode: z.enum(VAT_MODES),
  vat_id: z.string().nullable(),
  tax_number: z.string().nullable(),
  iban: z.string().nullable(),
  bic: z.string().nullable(),
  account_holder: z.string().nullable(),
  invoice_prefix: z.string(),
})
export type IssuerSnapshot = z.infer<typeof issuerSnapshotSchema>

/** `contractor_invoices.recipient_snapshot_json` — the Auftraggeber as he was at approval. */
export const recipientSnapshotSchema = z.object({
  legal_name: z.string(),
  address: addressSchema,
  vat_id: z.string().nullable(),
  tax_number: z.string().nullable(),
  handelsregister: z.string().nullable(),
  geschaeftsfuehrer: z.string().nullable(),
  kreditor_no: z.number().int().nullable(),
})
export type RecipientSnapshot = z.infer<typeof recipientSnapshotSchema>

/**
 * The six states of `contractor_invoices.status`, in the order the CHECK constraint of
 * migrations/0001_init.sql spells them. Here and not in the worker, because the query below validates
 * against them and the future client offers exactly this filter. `InvoiceStatus` in
 * src/worker/services/billing.ts is derived from this list, so there is one copy of the constraint.
 */
export const INVOICE_STATUSES = ['draft', 'submitted', 'rejected', 'approved', 'paid', 'cancelled'] as const

/** One of the six states — the type behind the list, so nothing restates the six literals. */
export type InvoiceStatusValue = (typeof INVOICE_STATUSES)[number]

/**
 * The values the queue filter accepts: the six states and the empty string. An untouched `<select>`
 * submits `?status=`, and that is the unfiltered queue, not a malformed request — while a status that
 * does not exist stays a 400 with the German sentence.
 */
const STATUS_FILTER_VALUES = [...INVOICE_STATUSES, ''] as const

/** GET /api/t/:id/rechnungen?status — the Auftraggeber's approval queue; no filter means every state. */
export const rechnungenQuerySchema = z.object({
  status: z.enum(STATUS_FILTER_VALUES, { message: 'Diesen Rechnungsstatus gibt es nicht.' }).optional(),
})
export type RechnungenQuery = z.infer<typeof rechnungenQuerySchema>

/** The filter the service takes: absent and empty are the same thing, namely every state. */
export function invoiceStatusFilter(query: RechnungenQuery): InvoiceStatusValue | undefined {
  return query.status === undefined || query.status === '' ? undefined : query.status
}

/**
 * GET /api/me/abrechnung?tenant_contractor_id=… — the "Jetzt abrechnen" screen. The id is required: the
 * screen is always about one Auftraggeber, and the service answers 404 for one the session does not own.
 */
export const abrechnungQuerySchema = z.object({
  tenant_contractor_id: id,
})
export type AbrechnungQuery = z.infer<typeof abrechnungQuerySchema>
